import { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '../api/client';
import { applyCompetition } from '../api/competitionApplication';
import { GRADE_OPTIONS } from '../constants/gradeOptions';
import { useI18n } from '../i18n';
import { buildApplicationPayload } from '../utils/applicationPayload';
import {
  formatPhoneInput,
  getBirthdateInputBounds,
  validateBirthdate,
  validateHeight,
  validatePhone,
} from '../utils/registrationValidation';

const INITIAL_FORM = {
  affiliation: '',
  dojo: '',
  coachName: '',
  name: '',
  phone: '',
  guardianPhone: '',
  birthdate: '',
  gender: '',
  grade: '',
  height: '',
};

function AddParticipantModal({ open, tournament, onClose, onSuccess }) {
  const { t } = useI18n();
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  const birthdateBounds = useMemo(() => getBirthdateInputBounds(), []);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose, submitting]);

  useEffect(() => {
    if (open) setForm(INITIAL_FORM);
  }, [open]);

  if (!open || !tournament) return null;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updatePhoneField = (field, value) => {
    updateField(field, formatPhoneInput(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const affiliation = form.affiliation.trim();
    const dojo = form.dojo.trim();
    const coachName = form.coachName.trim();
    const name = form.name.trim();

    if (!affiliation || !dojo || !coachName || !name) {
      alert(t('registration.alerts.requiredAffiliation'));
      return;
    }

    const phoneCheck = validatePhone(form.phone, t('registration.fields.phone'));
    if (!phoneCheck.valid) {
      alert(phoneCheck.message);
      return;
    }
    const guardianCheck = validatePhone(form.guardianPhone, t('registration.fields.guardianPhone'));
    if (!guardianCheck.valid) {
      alert(guardianCheck.message);
      return;
    }

    if (!form.gender) {
      alert(t('registration.alerts.selectGender'));
      return;
    }
    if (!form.grade) {
      alert(t('registration.alerts.selectGrade'));
      return;
    }

    const birthCheck = validateBirthdate(form.birthdate);
    if (!birthCheck.valid) {
      alert(birthCheck.message);
      return;
    }

    const heightCheck = validateHeight(form.height);
    if (!heightCheck.valid) {
      alert(heightCheck.message);
      return;
    }

    setSubmitting(true);
    try {
      const formData = {
        ...form,
        tournament: tournament.value ?? String(tournament.id),
      };
      const payload = buildApplicationPayload(formData, [tournament]);
      await applyCompetition(payload);
      onSuccess?.();
      onClose();
    } catch (error) {
      alert(getApiErrorMessage(error, t('registration.addModal.error')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="modal-dialog modal-dialog-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-participant-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <p className="modal-eyebrow">ADD PARTICIPANT</p>
            <h2 id="add-participant-title" className="modal-title">
              {t('registration.addModal.title')}
            </h2>
            <p className="modal-subtitle">{tournament.name}</p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="add-affiliation">
                {t('registration.fields.affiliation')} <span className="required">*</span>
              </label>
              <input
                id="add-affiliation"
                type="text"
                className="form-input"
                value={form.affiliation}
                onChange={(e) => updateField('affiliation', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-dojo">
                {t('registration.fields.dojo')} <span className="required">*</span>
              </label>
              <input
                id="add-dojo"
                type="text"
                className="form-input"
                value={form.dojo}
                onChange={(e) => updateField('dojo', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="add-coachName">
                {t('registration.fields.coachName')} <span className="required">*</span>
              </label>
              <input
                id="add-coachName"
                type="text"
                className="form-input"
                value={form.coachName}
                onChange={(e) => updateField('coachName', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-name">
                {t('registration.fields.name')} <span className="required">*</span>
              </label>
              <input
                id="add-name"
                type="text"
                className="form-input"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="add-phone">
                {t('registration.fields.phone')} <span className="required">*</span>
              </label>
              <input
                id="add-phone"
                type="tel"
                inputMode="numeric"
                className="form-input"
                placeholder="010-1234-5678"
                value={form.phone}
                onChange={(e) => updatePhoneField('phone', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-guardianPhone">
                {t('registration.fields.guardianPhone')} <span className="required">*</span>
              </label>
              <input
                id="add-guardianPhone"
                type="tel"
                inputMode="numeric"
                className="form-input"
                placeholder="010-1234-5678"
                value={form.guardianPhone}
                onChange={(e) => updatePhoneField('guardianPhone', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="add-birthdate">
                {t('registration.fields.birthdate')} <span className="required">*</span>
              </label>
              <input
                id="add-birthdate"
                type="date"
                className="form-input"
                min={birthdateBounds.min}
                max={birthdateBounds.max}
                value={form.birthdate}
                onChange={(e) => updateField('birthdate', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-grade">
                {t('registration.fields.grade')} <span className="required">*</span>
              </label>
              <select
                id="add-grade"
                className="form-select"
                value={form.grade}
                onChange={(e) => updateField('grade', e.target.value)}
              >
                {GRADE_OPTIONS.map((option) => (
                  <option key={option.value || 'placeholder'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <span className="form-label">
                {t('registration.fields.gender')} <span className="required">*</span>
              </span>
              <div className="gender-group gender-group-compact">
                <div
                  role="button"
                  tabIndex={0}
                  className={`gender-option${form.gender === '남' ? ' selected' : ''}`}
                  onClick={() => updateField('gender', '남')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      updateField('gender', '남');
                    }
                  }}
                >
                  {t('registration.gender.maleShort')}
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  className={`gender-option female${form.gender === '여' ? ' selected' : ''}`}
                  onClick={() => updateField('gender', '여')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      updateField('gender', '여');
                    }
                  }}
                >
                  {t('registration.gender.femaleShort')}
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-height">
                {t('registration.fields.height')} <span className="required">*</span>
              </label>
              <input
                id="add-height"
                type="number"
                className="form-input"
                min={80}
                max={250}
                value={form.height}
                onChange={(e) => updateField('height', e.target.value)}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-prev"
              onClick={onClose}
              disabled={submitting}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-submit" disabled={submitting}>
              {submitting ? t('registration.addModal.submitting') : t('registration.addModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddParticipantModal;
